import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'libs/core/src/lib/environments/environment';

/**
 * Lazily initializes Sentry by dynamically importing the SDK.
 * This keeps the heavy @sentry/angular package out of the initial chunk.
 */
export const initializeSentry = (name: string) => {
  import('@sentry/angular').then((Sentry) => {
    Sentry.init({
      dsn: 'https://b7f6393b8b94a3344c9632b97879f580@sentry.hobbiton.dev/2',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: 1.0,
      tracePropagationTargets: ['localhost', 'hobbiton.app', 'inshuwa.com','hobbiton.dev'],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: environment.debug
        ? `${name} Development`
        : `${name} Production`,
    });
  });
};

/**
 * Tags the Sentry scope with the resolved tenant, called once branding lands.
 *
 * `name` above is the portal type only. It used to interpolate environment.name, which worked
 * while every tenant had its own build - once one image serves them all, that would file every
 * tenant's errors under a single Sentry environment. A tag keeps them filterable without needing
 * an environment per tenant. The dynamic import resolves to the same cached module initializeSentry
 * used, and Sentry keeps global-scope tags across init, so ordering between the two doesn't matter.
 */
export const setSentryTenant = (tenant: string) => {
  import('@sentry/angular').then((Sentry) => Sentry.setTag('tenant', tenant));
};

/**
 * Provides Sentry TraceService via provideAppInitializer() so it can be
 * safely used in bootstrapApplication() providers. The TraceService is
 * instantiated eagerly (required by Sentry) and receives Router via inject().
 *
 * Usage: `providers: [...provideSentryTracing()]`
 */
export const provideSentryTracing = (): EnvironmentProviders =>
  makeEnvironmentProviders([
    provideAppInitializer(async () => {
      const router = inject(Router);
      const { TraceService } = await import('@sentry/angular');
      new TraceService(router);
    }),
  ]);
